import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft as ArrowLeft, FiSave as Save, FiAlertCircle as AlertCircle } from 'react-icons/fi';
import { createPrompt } from '../../../../services/aiOperationsApi';
import PromptEditor from '../components/prompts/PromptEditor';
import { parseTemplate } from '../../../../utils/templateParser';

const PromptCreatePage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: '',
    prompt_type: 'text',
    tags: [] as string[],
    labels: [] as string[],
    config: {
      temperature: 0.7,
      max_tokens: 1000
    }
  });

  const [tagInput, setTagInput] = useState('');
  const [labelInput, setLabelInput] = useState('');

  const templateMeta = parseTemplate(formData.template);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Prompt name is required');
      return;
    }

    if (!formData.template.trim()) {
      setError('Prompt template is required');
      return;
    }

    if (templateMeta.hasErrors) {
      setError('Please fix template errors before saving');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const result = await createPrompt({
        ...formData,
        name: formData.name.trim(),
        version: 1
      });

      // Navigate to the created prompt's detail page
      navigate(`/ai-operations/prompts/${result.prompt.id}`);
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : String(err)));
      console.error('Error creating prompt:', err);
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const addLabel = () => {
    if (labelInput.trim() && !formData.labels.includes(labelInput.trim())) {
      setFormData(prev => ({
        ...prev,
        labels: [...prev.labels, labelInput.trim()]
      }));
      setLabelInput('');
    }
  };

  const removeLabel = (label: string) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.filter(l => l !== label)
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/ai-operations/registry?tab=prompts')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Prompts
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Create New Prompt
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Create a new prompt template with variables and configuration
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Basic Information
          </h2>

          {/* Name */}
          <div>
            <label htmlFor="prompt-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Prompt Name <span className="text-red-500">*</span>
            </label>
            <input
                                                                                  id="prompt-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., customer-support-agent"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Use lowercase with hyphens (e.g., my-prompt-name)
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="prompt-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
                                                                                  id="prompt-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this prompt does..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Type */}
          <div>
            <label htmlFor="prompt-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Prompt Type
            </label>
            <select
                                                                                  id="prompt-type"
              value={formData.prompt_type}
              onChange={(e) => setFormData(prev => ({ ...prev, prompt_type: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="text">Text</option>
              <option value="chat">Chat</option>
              <option value="completion">Completion</option>
            </select>
          </div>
        </div>

        {/* Template Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Prompt Template <span className="text-red-500">*</span>
          </h2>
          <PromptEditor
            value={formData.template}
            onChange={(value) => setFormData(prev => ({ ...prev, template: value }))}
            label=""
            placeholder="Enter your prompt template here. Use {{variable_name}} for dynamic values."
          />
        </div>

        {/* Tags and Labels Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Tags & Labels
          </h2>

          {/* Tags */}
          <div>
            <label htmlFor="prompt-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                                                                                  id="prompt-tags"
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                         rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Add
              </button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900
                             text-blue-800 dark:text-blue-100 rounded text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-blue-600 dark:hover:text-blue-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Labels */}
          <div>
            <label htmlFor="prompt-labels" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Labels
            </label>
            <div className="flex gap-2 mb-2">
              <input
                                                                                  id="prompt-labels"
                type="text"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
                placeholder="Add a label (e.g., production)..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addLabel}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300
                         rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Add
              </button>
            </div>
            {formData.labels.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.labels.map(label => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900
                             text-green-800 dark:text-green-100 rounded text-sm"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => removeLabel(label)}
                      className="hover:text-green-600 dark:hover:text-green-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Configuration Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Model Configuration
          </h2>

          <div className="grid grid-cols-2 gap-4">
            {/* Temperature */}
            <div>
              <label htmlFor="prompt-temperature" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Temperature
              </label>
              <input
                                                                                  id="prompt-temperature"
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={formData.config.temperature}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, temperature: Number.parseFloat(e.target.value) }
                }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Max Tokens */}
            <div>
              <label htmlFor="prompt-max-tokens" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Tokens
              </label>
              <input
                                                                                  id="prompt-max-tokens"
                type="number"
                min="1"
                max="100000"
                step="1"
                value={formData.config.max_tokens}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  config: { ...prev.config, max_tokens: Number.parseInt(e.target.value) }
                }))}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/ai-operations/registry?tab=prompts')}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                     text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                     transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !formData.name || !formData.template || templateMeta.hasErrors}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg
                     hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Creating...' : 'Create Prompt'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PromptCreatePage;
